import { memo, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Download, ImageOff } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { BACKEND_URI } from "@/constants/config"
import { imageFileExtension, imageMimeTypeForFilename } from "@/features/chat/utils/image-file-mime"
import {
  isOtherBufferOfSameFile,
  sessionImageCacheKey,
} from "@/features/chat/utils/session-image-cache-key"
import { useTurnScopedFileContent } from "@/hooks/use-turn-scoped-file-content"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"

import { getBasenameFromPath, normalizeInlineImageSrc } from "./inline-session-image-utils"

// Cache blobs and object URLs across re-renders and mounts. The key names the
// scope, the filename and the exact ArrayBuffer the blob was built from, so a
// later turn that regenerates the file (Code Interpreter reuses names like
// chart.png) gets its own slot instead of showing or evicting the previous
// bytes, while a turn id changing under the same bytes keeps its object URL
// (#3474). Buffers come from the React Query cache and are stable per fetch.
const blobCache = new Map<string, Blob>()
const cacheTimestamps = new Map<string, number>()

// Object URL cache with reference counts so URLs survive re-mounts during streaming
const objectUrlCache = new Map<string, string>()
const objectUrlRefCount = new Map<string, number>()

const bufferIds = new WeakMap<ArrayBuffer, number>()
let nextBufferId = 0

function bufferId(buffer: ArrayBuffer): number {
  let id = bufferIds.get(buffer)
  if (id === undefined) {
    nextBufferId += 1
    id = nextBufferId
    bufferIds.set(buffer, id)
  }
  return id
}

function createObjectUrlForKey(cacheKey: string, blob: Blob): string {
  const url = URL.createObjectURL(blob)
  objectUrlCache.set(cacheKey, url)
  objectUrlRefCount.set(url, 1)
  return url
}

/**
 * Drop unreferenced slots for the same scope and filename built from other
 * bytes (an earlier fetch of the same file): a refetch must not keep a second
 * multi-MB copy alive until the TTL sweep.
 */
function evictUnreferencedSiblings(cacheKey: string): void {
  for (const key of Array.from(blobCache.keys())) {
    if (!isOtherBufferOfSameFile(cacheKey, key)) continue
    const url = objectUrlCache.get(key)
    if (url && (objectUrlRefCount.get(url) || 0) > 0) continue
    blobCache.delete(key)
    cacheTimestamps.delete(key)
    if (url) {
      objectUrlCache.delete(key)
      objectUrlRefCount.delete(url)
      URL.revokeObjectURL(url)
    }
  }
}

function releaseObjectUrlForKey(cacheKey: string): void {
  const url = objectUrlCache.get(cacheKey)
  if (!url) return
  const next = Math.max(0, (objectUrlRefCount.get(url) || 1) - 1)
  // Keep the URL cached even when unused; pruneCaches revokes it once expired.
  objectUrlRefCount.set(url, next)
}

// Simple safety limits for caches
const CACHE_MAX_ENTRIES = 100
const CACHE_TTL_MS = 5 * 60 * 1000

function pruneCaches(): void {
  const now = Date.now()

  // Remove expired entries
  for (const [key, ts] of cacheTimestamps) {
    if (now - ts <= CACHE_TTL_MS) continue
    blobCache.delete(key)
    cacheTimestamps.delete(key)
    const url = objectUrlCache.get(key)
    if (!url) continue
    const refCount = objectUrlRefCount.get(url) || 0
    if (refCount > 0) continue
    objectUrlCache.delete(key)
    objectUrlRefCount.delete(url)
    URL.revokeObjectURL(url)
  }

  // Enforce max size (oldest first)
  if (blobCache.size > CACHE_MAX_ENTRIES) {
    const entries = Array.from(cacheTimestamps.entries()).sort((a, b) => a[1] - b[1])
    let index = 0
    while (blobCache.size > CACHE_MAX_ENTRIES && index < entries.length) {
      const key = entries[index][0]
      index++
      if (!blobCache.has(key)) continue
      blobCache.delete(key)
      cacheTimestamps.delete(key)
      const url = objectUrlCache.get(key)
      if (!url) continue
      const refCount = objectUrlRefCount.get(url) || 0
      if (refCount > 0) continue
      objectUrlCache.delete(key)
      objectUrlRefCount.delete(url)
      URL.revokeObjectURL(url)
    }
  }

  // Clean up stray object URLs with no timestamp and no usage
  for (const [key, url] of objectUrlCache) {
    if (cacheTimestamps.has(key)) continue
    const refCount = objectUrlRefCount.get(url) || 0
    if (refCount > 0) continue
    objectUrlCache.delete(key)
    objectUrlRefCount.delete(url)
    URL.revokeObjectURL(url)
  }
}

// 1x1 transparent GIF placeholder to avoid broken image icon during streaming
const TRANSPARENT_GIF = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="

type InlineSessionImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  sessionId: string
  filenames: string[]
  isStreaming?: boolean
  /**
   * Identifies the chat turn this image belongs to: the message id, or a
   * stable slot key while the id is unknown. The finished-turn lookup is
   * cached per turn, so a 404 pinned for one turn cannot hide the same
   * filename registered by a later turn (assistant uploads keep the Code
   * Interpreter basename). Without a turn id the finished-turn policy does
   * not apply and the image keeps retrying like a streaming one.
   */
  turnId?: string
  /** The slot key the turn used before `turnId` became its message id; see useTurnScopedFileContent. */
  slotTurnId?: string
  /** When set, fetch images from this base (e.g. a saved artifact's document-content
   * endpoint) instead of /session-content/{sessionId}. */
  contentBaseUrl?: string
}

export const InlineSessionImage = memo(
  function InlineSessionImage({
    sessionId,
    filenames,
    src,
    alt,
    isStreaming,
    turnId,
    slotTurnId,
    contentBaseUrl,
    ...rest
  }: InlineSessionImageProps) {
    const { t } = useTranslation()
    const [objectUrl, setObjectUrl] = useState<string | null>(null)
    // Cache/fetch scope: document base (shared artifact) or the session id.
    const scope = contentBaseUrl || sessionId

    // Strip sandbox prefix from src to prevent fallback to invalid URLs
    const normalizedSrc = useMemo(() => normalizeInlineImageSrc(src), [src])

    const matchedFilename = useMemo(() => {
      if (!normalizedSrc) return null
      // Prefer direct inclusion match
      const direct = filenames.find((f) => normalizedSrc.includes(f))
      if (direct) return direct
      // Fallback: match by basename when LLM emits sandbox paths
      const srcBase = getBasenameFromPath(normalizedSrc)
      if (!srcBase) return null
      return filenames.includes(srcBase) ? srcBase : null
    }, [normalizedSrc, filenames])

    // Ensure we always fetch by a clean basename (strip sandbox prefix if present).
    // Fallback: if src is a sandbox path (sandbox:/mnt/data/...) not found in
    // assistant_files, try fetching by basename directly. This handles cases where
    // assistant_files has a mismatched name (e.g., artifact-{id}) but the file is
    // stored under its original name from the container path.
    const isSandboxSrc = typeof src === "string" && src.startsWith("sandbox:")
    const resolvedFilename = useMemo(() => {
      if (matchedFilename) return getBasenameFromPath(matchedFilename)
      if (isSandboxSrc && normalizedSrc) {
        const basename = getBasenameFromPath(normalizedSrc)
        if (basename && basename.includes(".")) return basename
      }
      return null
    }, [matchedFilename, normalizedSrc, isSandboxSrc])

    const externalUrl = useMemo(() => {
      if (!normalizedSrc) return null
      if (typeof window === "undefined") return null
      try {
        return new URL(normalizedSrc, window.location.href)
      } catch {
        return null
      }
    }, [normalizedSrc])

    const isExternalHttpImage = useMemo(() => {
      if (!externalUrl) return false
      if (typeof window === "undefined") return false
      const protocol = externalUrl.protocol
      const isHttp = protocol === "http:" || protocol === "https:"
      const isSameOrigin = externalUrl.origin === window.location.origin
      return isHttp && !isSameOrigin
    }, [externalUrl])

    // Build URL for React Query
    const fileUrl = useMemo(() => {
      if (!resolvedFilename || !scope) return undefined
      return contentBaseUrl
        ? `${contentBaseUrl}/${encodeURIComponent(resolvedFilename)}`
        : `${BACKEND_URI}/session-content/${sessionId}/${encodeURIComponent(resolvedFilename)}`
    }, [resolvedFilename, sessionId, contentBaseUrl, scope])

    // Fetch policy (retries while the turn streams, one fresh attempt once it
    // has finished, turn-scoped cache keys) lives in useTurnScopedFileContent.
    // Chat passes `isStreaming`; canvas passes nothing and gets no turn policy.
    // History: a 404 used to be re-requested dozens of times on re-mount (#2874).
    const turnFinished = isStreaming === false && !!turnId
    const { data: buffer, isError: fileUnavailable } = useTurnScopedFileContent(
      fileUrl,
      !!fileUrl,
      {
        isStreaming,
        turnId,
        slotTurnId,
      }
    )
    const cacheKey =
      resolvedFilename && scope && buffer
        ? sessionImageCacheKey(scope, resolvedFilename, bufferId(buffer))
        : null

    useEffect(() => {
      if (!cacheKey || !resolvedFilename || !buffer) {
        return
      }

      try {
        // One sweep per load: expired and surplus slots go before this one is
        // added (the new slot is referenced and cannot be swept anyway).
        pruneCaches()
        const mimeType = imageMimeTypeForFilename(resolvedFilename)
        // These bytes already have an object URL (a re-mount, or the turn id
        // changing under this mount): reuse it, no re-decode. The URL is the
        // source of truth: the TTL sweep may have dropped the blob entry while
        // the URL stayed referenced, and a replacement URL would orphan it.
        const cachedUrl = objectUrlCache.get(cacheKey)
        if (cachedUrl) {
          // No blob rebuild when the sweep dropped it: the download falls
          // back to the buffer itself.
          objectUrlRefCount.set(cachedUrl, (objectUrlRefCount.get(cachedUrl) || 0) + 1)
          cacheTimestamps.set(cacheKey, Date.now())
          setObjectUrl(cachedUrl)
        } else {
          // After this commit: the element may still show a sibling's URL
          // while the new one is being painted in.
          setTimeout(() => evictUnreferencedSiblings(cacheKey), 0)
          const blob = new Blob([buffer], { type: mimeType })
          blobCache.set(cacheKey, blob)
          cacheTimestamps.set(cacheKey, Date.now())
          setObjectUrl(createObjectUrlForKey(cacheKey, blob))
        }
      } catch (error) {
        logger.error("Failed to resolve session image", error as Error)
        setObjectUrl(null)
      }

      return () => {
        releaseObjectUrlForKey(cacheKey)
      }
    }, [buffer, cacheKey, resolvedFilename])

    if (!matchedFilename && isExternalHttpImage) {
      const href = externalUrl ? externalUrl.toString() : String(src || "")
      const label = alt || href
      return (
        <a href={href} rel="noopener noreferrer" target="_blank">
          {label}
        </a>
      )
    }

    const placeholderSrc = !objectUrl && !isExternalHttpImage ? TRANSPARENT_GIF : undefined
    const finalSrc = objectUrl || placeholderSrc || src
    const isPlaceholder = finalSrc === TRANSPARENT_GIF
    const finalAlt = objectUrl ? alt : undefined
    const cachedBlob = cacheKey ? (blobCache.get(cacheKey) ?? null) : null

    const handleDownload = () => {
      try {
        if (!resolvedFilename) return

        // Prefer existing object URL if present (already points to the correct Blob)
        let href: string | null = objectUrl
        let tempUrlToRevoke: string | null = null

        // If we don't have an object URL yet, try the cached blob for these bytes
        if (!href && cachedBlob) {
          href = URL.createObjectURL(cachedBlob)
          tempUrlToRevoke = href
        }

        // If still not available, try cached blob from React Query cache
        if (!href && buffer) {
          const mimeType = imageMimeTypeForFilename(resolvedFilename)
          const blob = new Blob([buffer], { type: mimeType })
          href = URL.createObjectURL(blob)
          tempUrlToRevoke = href
        }

        if (!href) return

        const anchor = document.createElement("a")
        anchor.href = href
        anchor.download = resolvedFilename
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()

        if (tempUrlToRevoke) {
          // Revoke only the temporary URL we created for download
          URL.revokeObjectURL(tempUrlToRevoke)
        }
      } catch (error) {
        logger.error("Failed to download session image", error as Error)
      }
    }

    const fileExtension = imageFileExtension(resolvedFilename ?? "")
    const tooltipText = `${t("answer.download")}${fileExtension ? ` ${fileExtension}` : ""}`

    if (!objectUrl && fileUnavailable && turnFinished && resolvedFilename) {
      // The assistant referenced a file that was never delivered. Say so
      // instead of leaving an invisible placeholder where the image should be.
      return (
        <span
          className="text-muted-foreground my-2 inline-flex items-center gap-1.5 text-xs"
          role="status"
        >
          <ImageOff aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          {t("answer.file_unavailable", {
            defaultValue: "The file {{filename}} could not be retrieved.",
            filename: resolvedFilename,
          })}
        </span>
      )
    }

    return (
      <div
        className={cn(
          "group relative my-3 inline-block overflow-hidden rounded-xl",
          isPlaceholder && "hidden"
        )}
      >
        <img alt={finalAlt} className="block rounded-xl" loading="lazy" src={finalSrc} {...rest} />
        {resolvedFilename && finalSrc && (
          <div className="absolute top-1 right-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <Tooltip>
              <TooltipTrigger
                disabled={!objectUrl && !cachedBlob}
                className={cn(
                  buttonVariants({ size: "icon", variant: "icon", className: "size-7" })
                )}
                onClick={handleDownload}
              >
                <Download className="size-5" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    )
  },
  (prev, next) => {
    // Avoid re-render unless relevant props change
    if (prev.sessionId !== next.sessionId) return false
    if (prev.contentBaseUrl !== next.contentBaseUrl) return false
    // The streaming → finished transition switches the fetch policy and the
    // fallback rendering above; an image that stays mounted must see it.
    if (prev.isStreaming !== next.isStreaming) return false
    if (prev.turnId !== next.turnId) return false
    if (prev.slotTurnId !== next.slotTurnId) return false
    if (prev.src !== next.src) return false
    if (prev.alt !== next.alt) return false
    if (prev.className !== next.className) return false
    if (!Object.is(prev.style, next.style)) return false

    // shallow compare filenames list length and entries
    if (prev.filenames.length !== next.filenames.length) return false
    for (let i = 0; i < prev.filenames.length; i++) {
      if (prev.filenames[i] !== next.filenames[i]) return false
    }

    return true
  }
)
